<script setup lang="ts">
import type { LegalDocument } from '../content/legal-content';

/**
 * Un documento legale, reso leggibile.
 *
 * Colonna stretta, paragrafi distanziati, titoli numerati con un'ancora
 * ciascuno: un documento del genere viene citato per punti («vedi il 6 dei
 * termini»), quindi ogni sezione deve avere un indirizzo proprio.
 */
defineProps<{ document: LegalDocument; version: string }>();
</script>

<template>
  <article class="legal">
    <header class="legal__head">
      <h1 class="legal__title">{{ document.title }}</h1>
      <p class="legal__summary">{{ document.summary }}</p>
      <p class="legal__version">Versione del {{ version }}</p>
    </header>

    <section v-for="section in document.sections" :id="section.id" :key="section.id">
      <!-- L'ancora è il titolo stesso: chi cita il documento copia il link da qui. -->
      <h2 class="legal__heading">
        <a :href="`#${section.id}`">{{ section.heading }}</a>
      </h2>
      <p v-for="(paragraph, index) in section.paragraphs" :key="index" class="legal__paragraph">
        {{ paragraph }}
      </p>
      <ul v-if="section.bullets" class="legal__list">
        <li v-for="(bullet, index) in section.bullets" :key="index">{{ bullet }}</li>
      </ul>
    </section>
  </article>
</template>

<style scoped>
.legal {
  max-width: 44rem;
  margin-inline: auto;
  padding-block: 2.5rem 4rem;
}

.legal__head {
  margin-bottom: 2.5rem;
  padding-bottom: 1.5rem;
  border-bottom: 1px solid var(--color-border);
}

.legal__title {
  margin: 0 0 0.5rem;
  font-size: clamp(1.8rem, 5vw, 2.4rem);
  letter-spacing: -0.02em;
}

.legal__summary {
  margin: 0 0 0.75rem;
  color: var(--color-muted);
  font-size: 1.05rem;
}

.legal__version {
  margin: 0;
  color: var(--color-faint);
  font-size: 0.8125rem;
  font-variant-numeric: tabular-nums;
}

.legal__heading {
  margin: 2rem 0 0.6rem;
  font-size: 1.0625rem;
  letter-spacing: -0.01em;
}

.legal__heading a {
  color: var(--color-heading);
  text-decoration: none;
}

.legal__heading a:hover {
  text-decoration: underline;
}

.legal__paragraph {
  margin: 0 0 0.85rem;
  color: var(--color-text);
  line-height: 1.65;
}

.legal__list {
  margin: 0 0 0.85rem;
  padding-left: 1.2rem;
  color: var(--color-text);
  line-height: 1.65;
}

.legal__list li {
  margin-bottom: 0.4rem;
}
</style>
